import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {
  SendMessageResponse,
  GetChatResponse,
  SendMessageRequest,
  Chat,
} from "enums/ChatType";
import { logout } from "helpers/auth-functions";
import { BusinessTestimonialResponseItem } from "interfaces/business-testimonial";
import {
  CreateLeadSubmissionParams,
  GenerateLeadSubmissionAIBriefPayload,
  GenerateLeadSubmissionPaymentLinkPayload,
  LeadSubmissionCollabsResponse,
  LeadSubmissionResponse,
  UpdateLeadSubmissionProgressPayload,
} from "interfaces/lead-submission";

const { REACT_APP_API_URL, REACT_APP_API_KEY } = process.env;

export const instance = axios.create({
  baseURL: REACT_APP_API_URL,
});

const getNormalizedRequestUrl = (url?: string): string => {
  return (url ?? "").replace(/^\/+/, "").replace(/^api\//, "");
};

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const requestUrl = getNormalizedRequestUrl(config.url);

  if (requestUrl.startsWith("leadSubmissions")) {
    delete (config.headers as Record<string, string>)?.Authorization;
    delete (config.headers as Record<string, string>)?.["x-api-key"];
    return config;
  }

  if (
    requestUrl.startsWith("reports") ||
    requestUrl.startsWith("business/onboarding") ||
    requestUrl.startsWith("payment/generate-payment-link") ||
    requestUrl.startsWith("sales/save-partial") ||
    requestUrl.startsWith("info/brand-metadata") ||
    requestUrl.startsWith("info/user-location")
  ) {
    config.headers["x-api-key"] = REACT_APP_API_KEY;
  } else {
    config.headers["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
  }
  return config;
});

instance.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    return response.data;
  },
  (error: any) => {
    if (axios.isCancel(error)) {
      console.log("Requisição cancelada:", error.message);
    } else if (error) {
      switch (error.response?.status) {
        case 401:
        case 403:
          const allowedRoutes = [
            "/login",
            "/pricing",
            "/onboarding",
            "/self-campaign",
            "/self-campaign/design",
            "/self-campaign/activate",
          ];
          if (!allowedRoutes.includes(window.location.pathname)) {
            window.location.href = "/login";
          }
          logout(true);
          break;
      }
    }
    return Promise.reject(error);
  },
);

export const postSignIn = (params: any) => {
  return instance.post(`auth/signin`, params);
};

export const getUserLoggedInfo = () => {
  return instance.post(`auth/user-logged-info`);
};

export const sendVerificationCode = (params: any) => {
  return instance.post(`auth/send-verification-code`, params);
};

export const postSignupCode = (params: any) => {
  return instance.post(`auth/sign-in-code`, params);
};

export const postVerifyCode = (params: any) => {
  return instance.post(`auth/verify-code`, params);
};

export const getBusinessesLoggedUser = () => {
  return instance.post(`business/get-businesses-logged-user`);
};

export const getTokenBySessionId = (params: { sessionId: string }) => {
  return instance.post(`auth/signup-session`, params);
};

export const sendForgotPasswordEmail = (params: any) => {
  return instance.post(`auth/forgot-password-send-code`, params);
};

export const sendForgotPasswordNewPassword = (params: any) => {
  return instance.post(`auth/forgot-password-new-password`, params);
};

export const getBusinessById = async (businessId: string) => {
  return await instance.get(`business/${businessId}`);
};

export const getBrandMetadata = () => {
  return instance.get(`info/brand-metadata`);
};

export const fetchLoggedUserLocation = () => {
  return instance.get(`info/user-location`);
};

export const postAddVenue = (params: any) => {
  return instance.post(`business/add-venue`, params);
};

export const updateBusinessInfo = (params: any) => {
  return instance.post(`business/update-info`, params);
};

export const updateVenue = (params: any) => {
  return instance.post(`business/update-venue`, params);
};

export const deleteVenue = (params: any) => {
  return instance.post(`business/delete-venue`, params);
};

export const uploadLogo = (params: any) => {
  return instance.post(`business/upload-logo`, params, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const removeLogo = (params: any) => {
  return instance.delete(`business/remove-logo`, {
    data: params,
  });
};

export const getSubscriptionInfo = (data: any) => {
  return instance.post(`business/subscription/get`, data);
};

export const postSocialMediaInstructions = (params: any) => {
  return instance.post(`business/social-media-instructions`, params);
};

export const fetchBusinessEscrow = (data: any) => {
  return instance.post(`business/escrow-balance`, data);
};

export const fetchUserActivities = (data: any) => {
  return instance.post(`business/user-activities`, data);
};

export const requestCancelSubscription = (params: any) => {
  return instance.post(`business/request-cancel-subscription`, params);
};

export const calculateAdditionalCharge = (params: any) => {
  return instance.post(`collabs/additional-charge-checkout`, params);
};

export const fetchCollabs = (params: any) => {
  return instance.post(`collabs/search-business-collab`, params);
};

export const getCollabById = (collabId: string) => {
  return instance.get(`collabs/business/${collabId}`);
};

export const connectSocialMedia = (params: any) => {
  return instance.post(`business/connect-social-media`, params);
};

export const connectTikTok = (params: any) => {
  return instance.post(`business/connect-tiktok`, params);
};

export const updateAiAutoAccept = (params: any) => {
  return instance.post(`business/update-ai-auto-accept`, params);
};

export const updatePaymentMethod = (params: any) => {
  return instance.post(`business/update-payment-method`, params);
};

export const favoriteCreator = (params: any) => {
  return instance.post(`business/favorite-creator`, params);
};

export const unfavoriteCreator = (params: any) => {
  return instance.post(`business/unfavorite-creator`, params);
};

export const fetchFavoritedCreators = (params: any) => {
  return instance.post(`business/get-favorites-creators`, params);
};

export const createBusinessCollab = (params: any) => {
  return instance.post(`collabs/create-business-collab`, params);
};

export const searchBusinessApplications = (params: any) => {
  return instance.post(`collabs/search-business-applications`, params);
};

export const searchBusinessApplicationsInbox = (params: any) => {
  return instance.post(`collabs/business-applications-inbox`, params);
};

export const reviewBusinessApplication = (params: any) => {
  return instance.post(`collabs/review-business-application`, params);
};

export const searchInvitedBusiness = (params: any) => {
  return instance.post(`collabs/search-invited-business`, params);
};

export const rescheduleBusinessApplication = (params: any) => {
  return instance.post(`collabs/reschedule-business-application`, params);
};

export const rescheduleApplicationTargetTime = (params: any) => {
  return instance.post(`collabs/reschedule-application-target-time`, params);
};

export const rescheduleApplicationSuggestedDates = (params: any) => {
  return instance.post(
    `collabs/reschedule-application-suggested-dates`,
    params,
  );
};

export type CancelBusinessBookingParams = {
  bookingId: string;
  collabId: string;
  reasonCategory:
    | "operational_issue"
    | "emergency"
    | "scheduling_error"
    | "no_longer_needed"
    | "severe_weather"
    | "other";
  reasonText: string;
  cancellationTimestamp: string;
  cancellationLocalTimestamp: string;
  timeBeforeEventMinutes: number;
  isLastMinute: boolean;
};

export const cancelBusinessBooking = (params: CancelBusinessBookingParams) => {
  return instance.post(`collabs/cancel-business-booking`, params);
};

export const fetchPendingCollabsCount = (params: any) => {
  return instance.post(`collabs/business-applications-count`, params);
};

export const fetchCollabsApplicationsEstimations = (params: any) => {
  return instance.post(`collabs/predict-applications`, params);
};

export const generateBriefAI = (params: any) => {
  return instance.post(`collabs/generate-brief-ai`, params);
};

export const saveCollabOnboarding = (params: any) => {
  return instance.post(`collabs/save-onboarding`, params);
};

export const checkLocationAvailability = (params: any) => {
  return instance.post(`collabs/location-availability`, params);
};

export const checkTierAvailability = (params: any) => {
  return instance.post(`collabs/tiers-availability`, params);
};

export const getCollabsCampaignList = (params: any) => {
  return instance.post(`collabs-campaign/get-list`, params);
};

export const updateCampaignCompensation = (params: any) => {
  return instance.post(`collabs-campaign/update-compensation`, params);
};

export const updateCampaignDeliverables = (params: any) => {
  return instance.post(`collabs-campaign/update-deliverables`, params);
};

export const getCollabCampaignById = (collabCampaignId: string) => {
  return instance.get(`collabs-campaign/${collabCampaignId}`);
};

export const postCollabsCampaign = (params: any) => {
  return instance.post(`collabs-campaign/upsert`, params);
};

export const deleteCollabsCampaign = (campaignId: string) => {
  return instance.delete(`collabs-campaign/${campaignId}`);
};

export const getPlacesByName = (params: any) => {
  return instance.post(`maps/search-places-by-name`, params);
};

export const createLeadSubmission = (
  params: CreateLeadSubmissionParams,
): Promise<LeadSubmissionResponse> => {
  return instance.post(`leadSubmissions`, params);
};

export const getLeadSubmissionById = (
  leadSubmissionId: string,
): Promise<Record<string, unknown>> => {
  return instance.get(`leadSubmissions/${leadSubmissionId}`);
};

export const updateLeadSubmissionProgress = (
  leadSubmissionId: string,
  params: UpdateLeadSubmissionProgressPayload,
): Promise<Record<string, unknown>> => {
  return instance.patch(`leadSubmissions/${leadSubmissionId}/progress`, params);
};

export const getLeadSubmissionCollabs = (
  leadSubmissionId: string,
): Promise<LeadSubmissionCollabsResponse> => {
  return instance.get(`leadSubmissions/${leadSubmissionId}/collabs`);
};

export const generateLeadSubmissionAIBrief = (
  leadSubmissionId: string,
  params: GenerateLeadSubmissionAIBriefPayload,
): Promise<Record<string, unknown>> => {
  return instance.post(
    `leadSubmissions/${leadSubmissionId}/generate-ai-brief`,
    params,
  );
};

export const generateLeadSubmissionPaymentLink = (
  leadSubmissionId: string,
  params: GenerateLeadSubmissionPaymentLinkPayload,
): Promise<Record<string, unknown>> => {
  return instance.post(
    `leadSubmissions/${leadSubmissionId}/generate-payment-link`,
    params,
  );
};

export const getBusinessTestimonials = (): Promise<
  BusinessTestimonialResponseItem[]
> => {
  return instance.get(`businessTestimonials`);
};

export const getPlaceById = (placeId: string, enrich?: boolean) => {
  const url = enrich ? `maps/${placeId}/enriched` : `maps/${placeId}`;
  return instance.get(url);
};

export const getFoodieCategories = () => {
  return instance.get(`info/foodie-categories`);
};

export const getInfluencers = (params: any) => {
  return instance.post(`influencer/list-filtered`, params);
};

export const getFoodieById = (foodieId: string) => {
  return instance.get(`user/get-by-id/${foodieId}`);
};

export const updatePassword = (params: any) => {
  return instance.post(`auth/update-password`, params);
};

export const getCollabsStats = (params: any) => {
  return instance.post(`reports/collabs-stats`, params);
};

export const getCollabsStatsByDate = (params: any) => {
  return instance.post(`reports/comments-stats`, params);
};

export const getSocialMediaStats = (params: any) => {
  return instance.post(`reports/social-media-stats`, params);
};

export const chargeAdditionalInfluencerCredits = (params: any) => {
  return instance.post(`payment/charge-additional-influencer-credits`, params);
};

export const generatePaymentLink = (params: any) => {
  return instance.post(`payment/generate-payment-link`, params);
};

export const getPaymentMethods = (params: any) => {
  return instance.post(`payment/customer-payment-methods`, params);
};

export const getSubscriptionCheckoutSession = (params: any) => {
  return instance.post(`payment/business-session-id`, params);
};

export const retrieveUserSocialMedia = (params: any) => {
  return instance.post(`user/retrieve-user-social-media`, params);
};

export const postFinishOnboarding = (params: any) => {
  return instance.post(`business/onboarding/finish`, params);
};

export const postPartialSaveOnboarding = (params: any) => {
  return instance.post(`sales/save-partial`, params);
};

export const getCreativeFreedomPromotion = (params: any) => {
  return instance.post(`promotions/generate-creative-freedom`, params);
};

export const getDiscountedOfferPromotion = (params: any) => {
  return instance.post(`promotions/generate-discounted-offer`, params);
};

export const getReservationPromotion = (params: any) => {
  return instance.post(`promotions/generate-reservation-promotion`, params);
};

export const getDeliveryPromotion = (params: any) => {
  return instance.post(`promotions/generate-delivery-promotion`, params);
};

export const postMustardBotChat = (params: any) => {
  return instance.post(`mustard-bot/chat`, params);
};

export const collabStartChat = (params: any) => {
  return instance.post("chats/collab-start-chat", params);
};

export const getSupportChat = (params: any) => {
  return instance.post(`chats/business-with-mustard`, params);
};

export const sendCollabInvite = (params: any) => {
  return instance.post(`collab-invites/send`, params);
};

export const acceptCollabInvite = (params: any) => {
  return instance.post(`collab-invites/accept`, params);
};

export const cancelCollabInvite = (data: any) => {
  return instance.post(`collab-invites/cancel`, data);
};

export const fetchAdditionalChargeCollabInvite = (data: any) => {
  return instance.post(`collab-invites/charge`, data);
};

export const getAIInfluencerSearchSuggestions = (params: any) => {
  return instance.post(`ai/search-creators`, params);
};

export const getChats = ({
  businessId,
  countOnly = false,
  offset = 0,
  limit = 20,
  sortBy = "newest",
  chatType = "all",
}: {
  businessId: string;
  countOnly?: boolean;
  offset?: number;
  limit?: number;
  sortBy?: SortBy;
  chatType?: "all" | "business" | "influencer" | "unreplied";
}) => {
  return instance.get<GetChatsResponse>(`chats`, {
    params: {
      businessId,
      countOnly,
      offset,
      limit,
      sortBy,
      chatType,
    },
  });
};

interface GetChatsResponse {
  chats: Chat[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

interface SuperMessengerChatsResponse {
  chats: Chat[];
  pagination: {
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
  };
}

export type SortBy = "newest" | "oldest";

export const getSuperMessengerAllChats = ({
  ownerFilter,
  sortBy,
  chatType,
  offset = 0,
  limit = 20,
  preloadChatId,
  term,
  signal,
}: {
  ownerFilter: string[];
  sortBy: SortBy;
  chatType: ("all" | "business" | "influencer" | "unreplied")[];
  offset?: number;
  limit?: number;
  preloadChatId?: string | null;
  term?: string;
  signal?: AbortSignal;
}) => {
  return instance.get<SuperMessengerChatsResponse>(`superchats`, {
    params: {
      ownerFilter,
      sortBy,
      chatType: chatType.join(","),
      offset,
      limit,
      preloadChatId,
      term,
    },
    signal,
  });
};

export const getChat = (
  id: string,
  businessId: string,
  abortController?: AbortController,
) => {
  return instance.get<GetChatResponse>(`chats/${id}`, {
    params: {
      businessId,
    },
    signal: abortController?.signal,
  });
};

export const getSuperMessengerChat = (
  id: string,
  abortController?: AbortController,
) => {
  return instance.get<GetChatResponse>(`superchats/${id}`, {
    params: {},
    signal: abortController?.signal,
  });
};

export const sendMessage = (id: string, data: SendMessageRequest) => {
  return instance.post<SendMessageResponse>(
    `chats/${id}/custom-messages`,
    data,
  );
};
export const sendSuperMessengerMessage = (
  id: string,
  data: SendMessageRequest,
) => {
  return instance.post<SendMessageResponse>(
    `superchats/${id}/custom-messages`,
    data,
  );
};

export const markChatAsRead = (chatId: string, businessId: string) => {
  return instance.post(`chats/${chatId}/read`, {
    params: {
      businessId,
    },
  });
};
export const markSuperMessengerChatAsRead = (chatId: string) => {
  return instance.post(`superchats/${chatId}/read`);
};

export const callChatAction = ({
  action,
  bookingId,
  messageId,
}: {
  action: string;
  bookingId: string;
  messageId: string;
}) => {
  return instance.post(`collabs-chat/trigger-action`, {
    action,
    bookingId,
    messageId,
  });
};

export const deleteMessage = (chatId: string, messageId: string) => {
  return instance.delete(`chats/${chatId}/messages/${messageId}`);
};

export const deleteSuperMessengerMessage = (
  chatId: string,
  messageId: string,
) => {
  return instance.delete(`superchats/${chatId}/messages/${messageId}`);
};

export const acceptAIMessage = (
  chatId: string,
  data: {
    messageId: string;
    executeTool: boolean;
    content: string;
    feedbackContent?: string;
  },
) => {
  return instance.post(`superchats/${chatId}/acceptAIMessage`, data);
};

export const assignChatToUsers = (chatId: string, assignees: string[]) => {
  return instance.post(`superchats/${chatId}/assign`, {
    assignees,
  });
};

export const markSuperMessengerChatAsReplied = (chatId: string) => {
  return instance.post(`superchats/${chatId}/system-replied`);
};

export const markSuperMessengerChatAsUnreplied = (chatId: string) => {
  return instance.post(`superchats/${chatId}/system-unreplied`);
};

export const archiveChat = (chatId: string) => {
  return instance.post(`chats/${chatId}/archive`);
};

export const getSuggestedSocialMedia = (params: any) => {
  return instance.post(`business/onboarding/suggest-socialmedia`, params);
};

export const disconnectSocialMedia = (params: any) => {
  return instance.post(`business/disconnect-social-media`, params);
};

export const finishBusinessOnboarding = (params: any) => {
  return instance.post(`business/finish-business-onboarding`, params);
};

export const generateBusinessDescriptionByWebsite = (params: any) => {
  return instance.post(`business/onboarding/generate-description`, params);
};

export const obtainBusinessLogoByWebsite = (params: any) => {
  return instance.post(`business/onboarding/retrieve-logo`, params);
};

export const getCities = (params: any) => {
  return instance.get(`info/cities`, {
    params,
  });
};

export const getCreatorsCategories = () => {
  return instance.get(`info/creators-categories`);
};
